import React, { useMemo, useState } from 'react';
import { SyndicationCompanyForm } from '@hum/icm-app/src/state';
import { Application, Company } from '@hum/types';
import { useForm } from '@hum/common/src/modules/form';
import { toast } from '@hum/common/src/modules/toast';
import { Box, Form, getMultiSelectValues } from '@hum/legacy-ui';
import { Button, ButtonType } from '@hum/ui-library';
import {
  UpdateSyndicationCompanyFormFields,
  validationSchema,
} from './FormFields';
import { updateCompany, useApiMutation } from '@hum/api';

const transformValues = (application: Application, company: Company) => ({
  syndicationCompany: {
    investmentTypes: application.investmentTypes || [],
    investmentAmount: application.investmentAmount || null,
    yearlyRevenueForSyndication:
      application.yearlyRevenueForSyndication || null,
  },
  company: {
    businessTypeForSyndication:
      company.businessTypeForSyndication === null
        ? undefined
        : company.businessTypeForSyndication,
  },
});

export const CompanySyndicationForm = ({
  onClose,
  company,
}: {
  onClose: () => void;
  company: Company;
}) => {
  const { mutate: updateCompanySyndication } = useApiMutation(updateCompany);
  const [loading, setLoading] = useState(false);
  const { application } = company;

  const updateSyndicationCompany = async (
    payload: SyndicationCompanyForm,
    resetForm: () => void
  ) => {
    try {
      setLoading(true);
      await Promise.all([
        updateCompanySyndication({
          uuid: company.uuid,
          ...payload.company,
          application: {
            ...payload.syndicationCompany,
            ...getMultiSelectValues(payload.syndicationCompany, [
              'investmentTypes',
            ]),
            investmentAmount:
              payload.syndicationCompany?.investmentAmount === null
                ? null
                : Number(payload.syndicationCompany?.investmentAmount),
            yearlyRevenueForSyndication:
              payload.syndicationCompany?.yearlyRevenueForSyndication ===
                null ||
              payload.syndicationCompany?.yearlyRevenueForSyndication ===
                undefined
                ? null
                : Number(
                    payload.syndicationCompany.yearlyRevenueForSyndication
                  ),
          } as Application,
        }),
      ]).then(() => {
        toast.success('Updated syndication details!');
        resetForm();
        onClose();
      });
    } catch (error: any) {
      console.error(error);
      const message = error?.message || 'Failed to update syndication details.';
      toast.error(message);
      return error;
    }
  };

  const initialValues = useMemo(() => transformValues(application, company), [
    application,
    company,
  ]);

  const form = useForm({
    initialValues,
    validationSchema,
    onSubmit: (payload: SyndicationCompanyForm, { resetForm }) =>
      updateSyndicationCompany(payload, resetForm),
  });

  const error = form.status;

  const closeForm = () => {
    onClose();
    form.resetForm();
  };

  return (
    <Form onSubmit={form.handleSubmit}>
      <UpdateSyndicationCompanyFormFields form={form} />
      <div>{error}</div>
      <Box justifySpaceBetween>
        <Button
          type={ButtonType.SECONDARY}
          onClick={closeForm}
          disabled={loading || form.isSubmitting}
        >
          Cancel
        </Button>

        <Button
          submit
          disabled={loading || form.isSubmitting}
          loading={form.isSubmitting}
          testId="admin-page:main-area:syndication:company-information-modal:update-button"
        >
          Update
        </Button>
      </Box>
    </Form>
  );
};
